/*
 * BARA BROS — DESKTOP DISCOVERY MEGA MENU
 * File: assets/bb-desktop-mega-menu.js
 * Owns: sections/bb-desktop-mega-menu.liquid
 *
 * This script is an ENHANCEMENT, never a dependency.
 *  - Without it, CSS :hover still opens the panels (see the `bbdm--js` rule in
 *    bb-desktop-mega-menu.css) and every link still navigates.
 *  - It makes no network requests. Opening the menu never touches Render,
 *    Postgres or any AI service. Personalization, if it ever arrives, calls
 *    window.BBMegaMenu.applyMerchandising() on already-rendered slots.
 *
 * Classic script, no modules, no framework, IE-safe-ish ES5+ syntax to match the
 * rest of assets/ (see bb-desktop-header-search.js).
 */
(function () {
  'use strict';

  var ROOT_SEL = '[data-bbdm-root]';
  var instances = [];

  /* ───────────────────────────────────────────────────────────
     ANALYTICS
     We do not build a new analytics platform. Each event is fanned out to the
     three sinks this theme already has, and every one is optional:
       1. a DOM CustomEvent  (`bb:mega_menu_open`) — always fires
       2. the theme's global pub/sub from assets/global.js, when present
       3. window.dataLayer, when a GTM-style array is present
     ─────────────────────────────────────────────────────────── */
  var EVENTS = {
    open: 'mega_menu_open',
    close: 'mega_menu_close',
    switch: 'mega_menu_category_switch',
    linkClick: 'mega_menu_link_click',
    cardImpression: 'mega_menu_card_impression',
    cardClick: 'mega_menu_card_click'
  };

  function emit(name, detail) {
    var payload = detail || {};
    payload.menu = payload.menu || 'desktop_discovery';

    try {
      document.dispatchEvent(
        new CustomEvent('bb:' + name, { detail: payload, bubbles: true })
      );
    } catch (e) {
      /* CustomEvent constructor missing: analytics is best-effort, never fatal. */
    }

    if (typeof window.publish === 'function') {
      try {
        window.publish('bb:' + name, payload);
      } catch (e) {
        /* theme pub/sub threw — not our problem to escalate */
      }
    }

    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      try {
        var gtm = { event: 'bb_' + name };
        for (var k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) gtm[k] = payload[k];
        }
        window.dataLayer.push(gtm);
      } catch (e) {
        /* ignore */
      }
    }
  }

  /* ───────────────────────────────────────────────────────────
     ONE MENU INSTANCE
     ─────────────────────────────────────────────────────────── */
  function MegaMenu(root) {
    this.root = root;
    this.items = Array.prototype.slice.call(root.querySelectorAll('[data-bbdm-item]'));
    this.openDelay = parseInt(root.getAttribute('data-bbdm-open-delay'), 10);
    this.closeDelay = parseInt(root.getAttribute('data-bbdm-close-delay'), 10);
    this.breakpoint = parseInt(root.getAttribute('data-bbdm-breakpoint'), 10) || 990;

    // Number(null) === 0 would silently disable both delays. Check explicitly.
    if (isNaN(this.openDelay)) this.openDelay = 120;
    if (isNaN(this.closeDelay)) this.closeDelay = 220;

    this.current = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.seenImpressions = {};
    this.pointerIsCoarse = false;

    if (!this.items.length) return;

    root.classList.add('bbdm--js');
    this.bind();
  }

  MegaMenu.prototype.isDesktop = function () {
    return window.innerWidth >= this.breakpoint;
  };

  MegaMenu.prototype.itemOf = function (el) {
    return el ? el.closest('[data-bbdm-item]') : null;
  };

  MegaMenu.prototype.keyOf = function (item) {
    return (item && item.getAttribute('data-bbdm-category-key')) || '';
  };

  MegaMenu.prototype.clearTimers = function () {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  /*
   * Publishes the space that actually exists below the nav bar as --bbdm-avail-h,
   * so the panel's max-height is measured rather than guessed at 78vh. One read,
   * batched before the panel is shown, and the value is cached until the viewport
   * changes — this never runs per frame and never interleaves read/write.
   */
  MegaMenu.prototype.syncAvailableHeight = function () {
    var sig = window.innerWidth + 'x' + window.innerHeight;
    if (this._availSig === sig) return;

    var rect = this.root.getBoundingClientRect();
    var barBottom = rect.top + rect.height + (window.scrollY || 0);
    var fromTop = Math.max(0, barBottom - (window.scrollY || 0));
    var avail = Math.round(window.innerHeight - fromTop - 20);

    // Never collapse the panel to nothing if the bar is scrolled off-screen.
    if (avail < 320) avail = 320;

    this.root.style.setProperty('--bbdm-avail-h', avail + 'px');
    this._availSig = sig;
  };

  MegaMenu.prototype.open = function (item, source) {
    if (!item || !this.isDesktop()) return;
    this.clearTimers();
    this.syncAvailableHeight();

    var wasOpen = !!this.current;
    var previous = this.current;
    if (previous === item) return;

    if (previous) this.deactivate(previous);
    this.activate(item);
    this.current = item;
    this.root.classList.add('is-open');

    var detail = { category: this.keyOf(item), source: source || 'pointer' };
    if (wasOpen) {
      detail.from = this.keyOf(previous);
      emit(EVENTS.switch, detail);
    } else {
      emit(EVENTS.open, detail);
    }

    this.reportImpressions(item);
  };

  /*
   * An item can carry MORE THAN ONE disclosure: the chevron always, plus the
   * category label itself when that menu entry has no destination and therefore
   * renders as a button. Every one of them has to report the same state, so this
   * is querySelectorAll, never querySelector.
   */
  function disclosuresOf(item) {
    return Array.prototype.slice.call(item.querySelectorAll('[data-bbdm-disclosure]'));
  }

  MegaMenu.prototype.setExpanded = function (item, expanded) {
    disclosuresOf(item).forEach(function (el) {
      el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  MegaMenu.prototype.activate = function (item) {
    item.classList.add('is-active');
    var panel = item.querySelector('[data-bbdm-panel]');
    if (panel) panel.classList.add('is-open');
    this.setExpanded(item, true);
  };

  MegaMenu.prototype.deactivate = function (item) {
    item.classList.remove('is-active');
    var panel = item.querySelector('[data-bbdm-panel]');
    if (panel) panel.classList.remove('is-open');
    this.setExpanded(item, false);
  };

  MegaMenu.prototype.close = function (source, returnFocus) {
    this.clearTimers();
    if (!this.current) return;

    var key = this.keyOf(this.current);
    var trigger = this.current.querySelector('[data-bbdm-disclosure]');
    this.deactivate(this.current);
    this.current = null;
    this.root.classList.remove('is-open');
    emit(EVENTS.close, { category: key, source: source || 'pointer' });

    if (returnFocus && trigger) trigger.focus();
  };

  MegaMenu.prototype.scheduleOpen = function (item) {
    var self = this;
    this.clearTimers();
    // Already open somewhere: switching categories is instant, which is the whole
    // point of "move horizontally without closing the experience".
    if (this.current) {
      this.open(item, 'pointer');
      return;
    }
    this.openTimer = setTimeout(function () {
      self.open(item, 'pointer');
    }, this.openDelay);
  };

  MegaMenu.prototype.scheduleClose = function () {
    var self = this;
    this.clearTimers();
    this.closeTimer = setTimeout(function () {
      self.close('pointer');
    }, this.closeDelay);
  };

  /* ───────────────────────────────────────────────────────────
     CARD IMPRESSIONS
     Fired once per card per page view, only when the card is actually visible
     inside an open panel.
     ─────────────────────────────────────────────────────────── */
  MegaMenu.prototype.reportImpressions = function (item) {
    var self = this;
    var cards = item.querySelectorAll('[data-bbdm-card]');
    if (!cards.length) return;

    if (typeof window.IntersectionObserver !== 'function') {
      Array.prototype.forEach.call(cards, function (card) {
        self.impress(card);
      });
      return;
    }

    if (!this.observer) {
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.impress(entry.target);
            self.observer.unobserve(entry.target);
          });
        },
        { threshold: 0.4 }
      );
    }

    Array.prototype.forEach.call(cards, function (card) {
      if (!self.seenImpressions[self.cardId(card)]) self.observer.observe(card);
    });
  };

  MegaMenu.prototype.cardId = function (card) {
    return (
      card.getAttribute('data-bbdm-category-key') + '::' + card.getAttribute('data-bbdm-slot')
    );
  };

  MegaMenu.prototype.impress = function (card) {
    var id = this.cardId(card);
    if (this.seenImpressions[id]) return;
    this.seenImpressions[id] = true;
    emit(EVENTS.cardImpression, this.cardDetail(card));
  };

  MegaMenu.prototype.cardDetail = function (card) {
    var titleEl = card.querySelector('[data-bbdm-card-title]');
    return {
      category: card.getAttribute('data-bbdm-category-key'),
      slot: parseInt(card.getAttribute('data-bbdm-slot'), 10),
      role: card.getAttribute('data-bbdm-card-role'),
      source: card.getAttribute('data-bbdm-card-source'),
      priority: parseInt(card.getAttribute('data-bbdm-priority'), 10),
      affinity_keys: card.getAttribute('data-bbdm-affinity-keys') || '',
      title: titleEl ? titleEl.textContent.trim() : '',
      url: card.tagName === 'A' ? card.getAttribute('href') : null
    };
  };

  /* ───────────────────────────────────────────────────────────
     BINDINGS
     ─────────────────────────────────────────────────────────── */
  MegaMenu.prototype.bind = function () {
    var self = this;
    var root = this.root;

    try {
      this.pointerIsCoarse =
        window.matchMedia && window.matchMedia('(hover: none)').matches;
    } catch (e) {
      this.pointerIsCoarse = false;
    }

    this.items.forEach(function (item) {
      item.addEventListener('mouseenter', function () {
        if (self.pointerIsCoarse) return;
        self.scheduleOpen(item);
      });

      item.addEventListener('mouseleave', function () {
        if (self.pointerIsCoarse) return;
        self.scheduleClose();
      });

      // Click fallback + touch-capable laptops. Bind EVERY disclosure on the item:
      // the chevron, plus the category label when it has no destination.
      disclosuresOf(item).forEach(function (disclosure) {
        disclosure.addEventListener('click', function (evt) {
          evt.preventDefault();
          evt.stopPropagation();
          if (self.current === item) {
            self.close('click');
          } else {
            self.open(item, 'click');
          }
        });
      });
    });

    // Keep the panel alive while the pointer is anywhere inside the menu.
    root.addEventListener('mouseenter', function () {
      if (!self.pointerIsCoarse) self.clearTimers();
    });
    root.addEventListener('mouseleave', function () {
      if (!self.pointerIsCoarse) self.scheduleClose();
    });

    // Focus that leaves the whole component closes it. `focusout` fires before
    // the new element is focused, hence the deferred check.
    root.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!root.contains(document.activeElement)) self.close('focusout');
      }, 0);
    });

    // Tabbing across the bar must NOT auto-open every panel — that would fire a
    // switch event per keystroke and trap the keyboard in card lists. Focus
    // landing on a different category just closes what was open; the panel opens
    // deliberately, via ArrowDown or the chevron.
    root.addEventListener('focusin', function (evt) {
      if (!self.current) return;
      if (self.current.contains(evt.target)) return;
      self.close('focus_move');
    });

    root.addEventListener('keydown', function (evt) {
      self.onKeydown(evt);
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && self.current) self.close('escape', true);
    });

    document.addEventListener(
      'click',
      function (evt) {
        if (!self.current) return;
        if (root.contains(evt.target)) return;
        self.close('outside_click');
      },
      true
    );

    // Analytics on the way out. Capture phase so it still records when a click
    // immediately navigates away.
    root.addEventListener(
      'click',
      function (evt) {
        var card = evt.target.closest ? evt.target.closest('[data-bbdm-card]') : null;
        if (card) {
          emit(EVENTS.cardClick, self.cardDetail(card));
          return;
        }
        var link = evt.target.closest ? evt.target.closest('[data-bbdm-nav-link]') : null;
        if (link) {
          emit(EVENTS.linkClick, {
            category: link.getAttribute('data-bbdm-category-key'),
            level: link.getAttribute('data-bbdm-link-role'),
            label: link.textContent.trim(),
            url: link.getAttribute('href')
          });
        }
      },
      true
    );

    // Crossing the breakpoint mid-session must not strand an open panel behind
    // a display:none section.
    window.addEventListener('resize', function () {
      self._availSig = null;
      if (!self.isDesktop() && self.current) {
        self.close('breakpoint');
      } else if (self.current) {
        self.syncAvailableHeight();
      }
    });

    // The theme editor re-renders sections; drop stale state.
    document.addEventListener('shopify:section:unload', function (evt) {
      if (evt.target && evt.target.contains(root)) self.close('section_unload');
    });
  };

  MegaMenu.prototype.onKeydown = function (evt) {
    var item = this.itemOf(evt.target);
    var isTopLevel =
      evt.target.hasAttribute &&
      (evt.target.hasAttribute('data-bbdm-disclosure') ||
        evt.target.getAttribute('data-bbdm-link-role') === 'l1');

    switch (evt.key) {
      case 'ArrowDown':
        if (item && isTopLevel) {
          evt.preventDefault();
          this.open(item, 'keyboard');
          var first = item.querySelector('[data-bbdm-panel] a');
          if (first) first.focus();
        }
        break;

      case 'ArrowRight':
      case 'ArrowLeft':
        if (!isTopLevel) break;
        evt.preventDefault();
        this.moveTopLevel(evt.key === 'ArrowRight' ? 1 : -1, evt.target);
        break;

      case 'Home':
      case 'End':
        if (!isTopLevel) break;
        evt.preventDefault();
        this.focusTopLevelAt(evt.key === 'Home' ? 0 : -1);
        break;

      case 'Tab':
        // Shift+Tab off the first trigger should not leave a panel hanging open.
        if (isTopLevel && evt.shiftKey && this.current) this.close('keyboard');
        break;

      default:
        break;
    }
  };

  MegaMenu.prototype.topLevelLinks = function () {
    return Array.prototype.slice.call(
      this.root.querySelectorAll('[data-bbdm-link-role="l1"]')
    );
  };

  MegaMenu.prototype.moveTopLevel = function (direction, fromEl) {
    var links = this.topLevelLinks();
    var currentItem = this.itemOf(fromEl);
    var index = -1;
    links.forEach(function (link, i) {
      if (link.closest('[data-bbdm-item]') === currentItem || link === fromEl) index = i;
    });
    if (index === -1) return;
    var next = (index + direction + links.length) % links.length;
    links[next].focus();
  };

  MegaMenu.prototype.focusTopLevelAt = function (index) {
    var links = this.topLevelLinks();
    if (!links.length) return;
    var target = index === -1 ? links[links.length - 1] : links[index];
    if (target) target.focus();
  };

  /* ───────────────────────────────────────────────────────────
     PUBLIC API — the future personalization seam
     A precomputed payload rewrites already-rendered slots. It cannot add,
     remove or reorder navigation links, and nothing here ever fetches.
     ─────────────────────────────────────────────────────────── */
  function slotsFor(categoryKey) {
    var sel = '[data-bbdm-slots]';
    if (categoryKey) {
      sel += '[data-bbdm-category-key="' + String(categoryKey).replace(/"/g, '') + '"]';
    }
    return Array.prototype.slice.call(document.querySelectorAll(sel));
  }

  function applyCard(card, data) {
    if (!card || !data) return false;

    if (data.title) {
      var titleEl = card.querySelector('[data-bbdm-card-title]');
      if (titleEl) titleEl.textContent = data.title;
    }

    if (typeof data.subtitle === 'string') {
      var subEl = card.querySelector('[data-bbdm-card-sub]');
      if (subEl) {
        subEl.textContent = data.subtitle;
        subEl.hidden = data.subtitle === '';
      }
    }

    if (typeof data.badge === 'string') {
      var badgeEl = card.querySelector('[data-bbdm-card-badge]');
      if (badgeEl) {
        badgeEl.textContent = data.badge;
        badgeEl.hidden = data.badge === '';
      }
    }

    // Only same-origin or root-relative destinations. A personalization payload
    // must never be able to turn navigation into an offsite redirect.
    if (data.url && /^\/(?!\/)/.test(data.url) && card.tagName === 'A') {
      card.setAttribute('href', data.url);
    }

    if (data.image_url) {
      var img = card.querySelector('.bbdm__card-img');
      if (img) {
        img.removeAttribute('srcset');
        img.setAttribute('src', data.image_url);
      }
    }

    if (data.preset) card.setAttribute('data-bbdm-card-preset', data.preset);
    card.setAttribute('data-bbdm-personalized', 'true');
    return true;
  }

  window.BBMegaMenu = {
    version: 1,

    /** Every menu instance on the page. */
    instances: instances,

    /** Card elements for a category (or all categories when omitted). */
    getSlots: function (categoryKey) {
      var out = [];
      slotsFor(categoryKey).forEach(function (container) {
        out = out.concat(
          Array.prototype.slice.call(container.querySelectorAll('[data-bbdm-card]'))
        );
      });
      return out;
    },

    /**
     * Rewrite merchandising slots in place.
     *
     * payload = {
     *   category_key: 'bara art',
     *   cards: [{ slot: 0, title, subtitle, badge, url, image_url, preset }]
     * }
     *
     * Returns the number of slots changed. Unknown slots are ignored rather than
     * created — the rendered card count is the merchant's decision, not the
     * ranking layer's.
     */
    applyMerchandising: function (payload) {
      if (!payload || !payload.cards || !payload.cards.length) return 0;
      var containers = slotsFor(payload.category_key);
      var changed = 0;

      containers.forEach(function (container) {
        payload.cards.forEach(function (data) {
          var card = container.querySelector(
            '[data-bbdm-card][data-bbdm-slot="' + parseInt(data.slot, 10) + '"]'
          );
          if (applyCard(card, data)) changed++;
        });
      });

      if (changed) {
        emit('mega_menu_personalized', {
          category: payload.category_key || 'all',
          slots: changed,
          profile_confidence: payload.profile_confidence || null
        });
      }
      return changed;
    },

    /** Close whatever is open. Exposed for other theme components. */
    closeAll: function () {
      instances.forEach(function (inst) {
        inst.close('api');
      });
    }
  };

  /* ───────────────────────────────────────────────────────────
     BOOT
     ─────────────────────────────────────────────────────────── */
  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SEL);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__bbdmBound) return;
      root.__bbdmBound = true;
      instances.push(new MegaMenu(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });
})();
